import React, {useEffect, useState} from 'react';
import WaterSystemsTable from "../../Components/WaterSystemsTable/WaterSystemsTable";
import {apolloClient} from '../../GraphQL/config';
import {QUERY_WATER_SYSTEM_STATUSES, QUERY_WATER_SYSTEM_STATUSES_NO_INPUT, QUERY_WATER_SYSTEM_STATUSES_NO_INPUT_PROD, QUERY_WATER_SYSTEM_STATUSES_PROD} from "../../GraphQL/WaterSystemsPage/queries";
import {Severities} from "../../Enums/MonitoringPage/severityTypes";
import {meterTypesDictionary} from "./meterTypeDictionary";
import {CLOSED_FACTOR, WARNING_FACTOR} from "../../Constants/constants";
import {defaultSystemStates} from "../../General/SystemLifeCycleStates";
import {getAllAccountsCountries} from "../../General/Countries";
import {Spinner} from "react-bootstrap";
import './WaterSystemsPage.css';

export default function WaterSystemPage() {
    const [waterSystemsData, setWaterSystemsData] = useState([]);
    const refreshRate = 60000;
    const [isRequestInProgress, setIsRequestInProgress] = useState(false);
    const [selectedSystemStates, setSelectedSystemStates] = useState(defaultSystemStates);
    const [countriesFilterOptions, setCountriesFilterOptions] = useState([]);

    const valveTypes = {
        0: 'Solenoid Plastic',
        1: 'Solenoid Metal',
        2: 'Ball Valve',
        3: 'Butterfly',
    }

    const getAllWaterSystems = async (isFromInterval, selectedSystemStatesValues) => {
        if (!isFromInterval) {
            setIsRequestInProgress(true);
        }
        let systemStateStringArr = [];
        if (selectedSystemStatesValues) {
            selectedSystemStatesValues.forEach((systemState) => {
                systemStateStringArr.push(systemState.value);
            })
        }
        let query;
        const currentEnv = process.env.REACT_APP_ENV;
        if (systemStateStringArr.length === 0) {
            query = currentEnv === 'prod' ? QUERY_WATER_SYSTEM_STATUSES_NO_INPUT_PROD : QUERY_WATER_SYSTEM_STATUSES_NO_INPUT;
        } else {
            query = currentEnv === 'prod' ? QUERY_WATER_SYSTEM_STATUSES_PROD : QUERY_WATER_SYSTEM_STATUSES;
        }
        await apolloClient.query({
            query: query, variables: {
                lifecyclePhases: systemStateStringArr
            }
        }).then(res => {
            let data = [];
            let response = res.data?.WaterSystemStatuses?.select;

            if (!response) {
                console.error(`Data from getAllWaterSystems is empty`);
                setWaterSystemsData([]);
                setIsRequestInProgress(false);
                return;
            }
            response.forEach(waterSystem => {
                let policyData = JSON.parse(waterSystem.policyData);
                let pipeDiameter = meterTypesDictionary[waterSystem.valveType] ? meterTypesDictionary[waterSystem.valveType].pipeDiameter : 'Unknown';
                let valveType = valveTypes[waterSystem.typeOfValve] && waterSystem.valvesCount > 0 ? valveTypes[waterSystem.typeOfValve] : 'No Valve';
                let meterType = meterTypesDictionary[waterSystem.valveType] ?
                    `${meterTypesDictionary[waterSystem.valveType].displayName} (${meterTypesDictionary[waterSystem.valveType].meterType})` : 'Unknown';

                data.push({
                    waterSystemId: waterSystem.waterSystemId,
                    waterSystemName: waterSystem.waterSystemName,
                    siteId: waterSystem.siteId,
                    siteName: waterSystem.siteName,
                    siteCountry: waterSystem.siteCountry,
                    organizationId: waterSystem.organizationId,
                    organizationName: waterSystem.organizationName,
                    pipeDiameter: pipeDiameter,
                    valveType: valveType,
                    meterType: meterType,
                    dataMode: getRowDataMode(waterSystem.httpMode, waterSystem.httpModeCurrent, waterSystem.emnifyId),
                    policyId: waterSystem.policyId,
                    policyName: waterSystem.policyName,
                    policyType: waterSystem.policyType,
                    deviceRn: waterSystem.hwId,
                    powerSource: waterSystem.powerSource,
                    valveStatus: getRowValveStatus(waterSystem),
                    leakStatus: (waterSystem.innerType != null) ? Severities[Math.min(waterSystem.innerType, 2)] : 'No Leak',
                    autoWaterShutoff: waterSystem.valvesCount <= 0 ? 2 : policyData?.auto_shutoff,//2 in case there is no valve because the value does not exist in backend(only 0 and 1 exists)
                    detectionMode: getRowDetectionMode(policyData),
                    systemInstallationDate: waterSystem.systemInstallationDate,
                    systemReplacementDate: waterSystem.systemReplacementDate,
                    cellNum: waterSystem.cellNum,
                    currentVersion: waterSystem.currentVersion,
                    batStat: waterSystem.batStat,
                    batteryVoltage: waterSystem.batteryVoltage,
                    username: `${waterSystem.userFirstName} ${waterSystem.userLastName}`,
                    lastKeepAlive: waterSystem.lastKeepAlive,
                    thresholds: getThresholds(waterSystem, policyData),
                    lifecyclePhase: waterSystem.lifecyclePhase
                })
            });
            setIsRequestInProgress(false);
            setWaterSystemsData(data);
        })
    }

    const getAllWaterSystemsPeriodically = async () => {
        await getAllWaterSystems(true, selectedSystemStates);
    }

    const getRowValveStatus = waterSystemData => {
        if (!valveTypes[waterSystemData.typeOfValve] || waterSystemData.valvesCount <= 0) {
            return 'N/A';
        }
        if (waterSystemData.valveError) {
            return 'Valve Error';
        }
        if (waterSystemData.manualOverride) {
            return 'Physical Override';
        } else if (waterSystemData.valveStatus === 0) {
            return 'Open';
        } else {
            return 'Closed';
        }
    }

    const getRowDataMode = (httpMode, httpModeCurrent, emnifyId) => {
        if (httpMode === 1 && httpModeCurrent === 1 && (emnifyId === 0 || emnifyId === null)) {
            return 'Data Mode';
        }

        if (httpMode === 1 && httpModeCurrent === 0 && (emnifyId === 0 || emnifyId === null)) {
            return 'Failure';
        }

        if (httpMode === 0 && httpModeCurrent === 0) {
            return 'SMS';
        }

        if (httpMode === 1 && httpModeCurrent === 0 && emnifyId === 1) {
            return 'Fallback';
        }

        return 'Unknown';
    }

    const getRowDetectionMode = policyData => {
        let detectionMode;

        if (policyData?.detection_mode) {
            if (policyData.detection_mode.type === 'fixed') {
                if (policyData.detection_mode.algo_mode === 0) {
                    detectionMode = 'Fixed - Event Based'
                } else if (policyData.detection_mode.algo_mode === 1 ||
                    !policyData.detection_mode.hasOwnProperty('algo_mode')) {
                    detectionMode = 'Fixed - Cumulative'
                }
            } else {
                detectionMode = policyData.detection_mode.type
            }
        }

        return detectionMode;
    }

    const getThresholds = (waterSystemData, policyData) => {
        const pulseRatio = waterSystemData.pulseRatio > 0 ? waterSystemData.pulseRatio : meterTypesDictionary[waterSystemData.valveType]?.pulseRatio;
        if (policyData.detection_mode.type === "adaptive") {
            return `W: ${Math.round(waterSystemData.vmaxPulseCount * pulseRatio * WARNING_FACTOR)},
                C: ${Math.round(waterSystemData.vmaxPulseCount * pulseRatio * CLOSED_FACTOR)}`
        } else {
            return `W: ${Math.floor(policyData.detection_mode.thresholds.warning * pulseRatio)},
                C: ${Math.floor(policyData.detection_mode.thresholds.close * pulseRatio)}`
        }
    }

    const getCountriesFilter = async () => {
        let countries = [];

        await getAllAccountsCountries().then((response) => {
            response.forEach(country => {
                countries.push({value: country, label: country});
            })
        });

        setCountriesFilterOptions(countries);
    }

    useEffect(() => {
        getAllWaterSystems(false, selectedSystemStates);
        getCountriesFilter();
    }, [])

    // Refresh the display every 60 seconds
    useEffect(() => {
            const intervalId = setInterval(() => {
                if (!isRequestInProgress) {
                    getAllWaterSystemsPeriodically();
                }
            }, refreshRate)

            return () => clearInterval(intervalId);
        }, [selectedSystemStates, isRequestInProgress]
    );

    return countriesFilterOptions.length > 0 ?
        <WaterSystemsTable
            setSelectedSystemStates={setSelectedSystemStates}
            getAllWaterSystems={getAllWaterSystems}
            isRequestInProgress={isRequestInProgress}
            data={waterSystemsData}
            countriesFilterOptions={countriesFilterOptions}/>
        :
        <Spinner animation='border' variant='primary'/>
}
